import Modal from '@/common/components/Modal';
import { ModalMessageSimple } from '@/common/components/ModalMessageSimple';
import { FormTransformers } from '@/common/utils/formTransformers';
import { useCreateGovernmentAllocationEntryProductCategory } from '@/features/privateCompanies/hooks/useCreateGovernmentAllocationEntryProductCategory';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import {
  entryProductCategoryCurrencySchema,
  entryProductCategoryQuantitySchema,
  GovernmentAllocationEntryProductCategoryCurrencyValidationSchema,
  GovernmentAllocationEntryProductCategoryQuantityValidationSchema,
} from '../../schemas/update.schema';
import { EntryProductCategory } from './EntryProductCategory';
import { EntryProductCategoryQuantity } from './EntryProductCategoryQuantity';
import { ClientCompanyAllowedCategoryAndProductData } from '@/features/privateCompanies/types/allowedCategoriesAndProducts.type';
import { ControlledByType } from '@/features/privateCompanies/types/companyGovernmentAllocation.type';

interface CreateEntryProductCategoryModalProps {
  companyBranchId?: string;
  allowedCategorySummary?: ClientCompanyAllowedCategoryAndProductData & { id: string } | undefined;
  onDismiss: VoidFunction;
  controlledBy?: ControlledByType;
}

const CreateEntryProductCategoryModal = ({ companyBranchId, allowedCategorySummary, onDismiss, controlledBy = 'currency' }: CreateEntryProductCategoryModalProps) => {
  const { id } = useParams();

  const queryClient = useQueryClient();

  const [messages, setMessages] = useState<string[] | boolean>(false);

  const schema = controlledBy === 'quantity' ? entryProductCategoryQuantitySchema : entryProductCategoryCurrencySchema;

  const {
    mutate: createEntryProductCategory,
    isPending: createEntryProductCategoryIsPending,
  } = useCreateGovernmentAllocationEntryProductCategory({
    governmentAllocationId: id,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['companyGovernmentAllocationEntry', id] });
      queryClient.invalidateQueries({ queryKey: ['clientCompanyBranchAvailableToAllocateSummary'] })
      onDismiss();
      reset();
    },
    onError: (error) => {
      FormTransformers.errorsTransformer({ setError, setMessages })(error);
    },
  });

  const formMethods = useForm<GovernmentAllocationEntryProductCategoryCurrencyValidationSchema | GovernmentAllocationEntryProductCategoryQuantityValidationSchema>({
    resolver: zodResolver(schema),
    defaultValues: {},
  });

  const {
    handleSubmit,
    setError,
    reset,
  } = formMethods;

  const onSubmit: SubmitHandler<any> = (form) => createEntryProductCategory({
    ...form,
    product_category: allowedCategorySummary,
  });

  useEffect(() => {
    if (allowedCategorySummary?.id) {
      reset();
    }
  }, [allowedCategorySummary?.id]);

  return (
    <>
      <Modal
        isOpen={!!allowedCategorySummary}
        setIsOpen={(value) => {
          if (!value) {
            onDismiss();

            setTimeout(reset, 500);
          }
        }}
        title={`Detalhando crédito por serviço`}
        showClose
        useCancellableFooter
        className="min-w-[640px] overflow-hidden"
        onConfirm={handleSubmit(onSubmit)}
        confirmButtonText="Salvar"
        confirmButtonClassName="bg-primary-blue-hover"
        disabled={createEntryProductCategoryIsPending}
        confirmButtonIsLoading={createEntryProductCategoryIsPending}
        body={(
          <FormProvider {...formMethods}>
            <div className="flex flex-col gap-6 text-white-800">
              <div className="max-h-[calc(100vh-10rem)] min-h-[360px] overflow-auto flex flex-col gap-6 pe-2">
                {allowedCategorySummary && controlledBy === 'quantity' ? (
                  <EntryProductCategoryQuantity
                    entryProductCategory={{
                      id: '',
                      product_category: {
                        id: allowedCategorySummary.id,
                        display_name: allowedCategorySummary.display_name,
                        products_count: ''
                      },
                      total_value: '',
                      total_quantity: '',
                      entry_products: [],
                    }}
                    allowedCategorySummary={allowedCategorySummary}
                    companyBranchId={companyBranchId}
                    disabled={false}
                  />
                ) : allowedCategorySummary && (
                  <EntryProductCategory
                    entryProductCategory={{
                      id: '',
                      product_category: {
                        id: allowedCategorySummary.id,
                        display_name: allowedCategorySummary.display_name,
                        products_count: ''
                      },
                      total_value: '',
                      entry_products: [],
                    }}
                    allowedCategorySummary={allowedCategorySummary}
                    companyBranchId={companyBranchId}
                    disabled={false}
                  />
                )}
              </div>
            </div>
          </FormProvider>
        )}
      />

      {messages && (
        <ModalMessageSimple
          messages={messages}
          setMessages={setMessages}
          isError
        />
      )}
    </>
  );
}

export default CreateEntryProductCategoryModal;
